/** Draws a background read at once from the cache, then refreshes it on mount,
 *  when `refreshKey` changes, or (with `maxAgeMs`) only once it is that old. A
 *  change of workspace never shows the previous workspace's answer, even for the
 *  render before the effect runs. */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  backgroundReadKey,
  currentBackgroundReadScope,
  peekBackgroundRead,
  readInBackground,
} from '../services/BackgroundReadCache';

export function useBackgroundRead<T>(
  workspaceId: string | undefined,
  read: { name: string; load: () => Promise<T> } | null,
  { maxAgeMs = 0, refreshKey, persist = true }: { maxAgeMs?: number; refreshKey?: string; persist?: boolean } = {},
): T | undefined {
  const name = read?.name ?? '';
  const enabled = read !== null;
  const scope = useMemo(() => (enabled ? currentBackgroundReadScope(workspaceId) : null), [workspaceId, enabled]);
  const key = scope ? backgroundReadKey(scope, name) : '';
  const loader = useRef(read?.load);
  // Declared before the read effect, so it always runs with the latest loader.
  useEffect(() => { loader.current = read?.load; });
  const [result, setResult] = useState<{ key: string; data: T | undefined }>(() => ({
    key,
    data: peekBackgroundRead<T>(scope, name)?.data,
  }));

  useEffect(() => {
    if (!scope || !loader.current) return;
    let cancelled = false;
    const cached = peekBackgroundRead<T>(scope, name);
    setResult({ key, data: cached?.data });
    readInBackground(scope, name, () => loader.current!(), { maxAgeMs, persist })
      .then((data) => { if (!cancelled) setResult({ key, data }); })
      .catch(() => { if (!cancelled) setResult({ key, data: undefined }); });
    return () => { cancelled = true; };
  // `scope` and `name` are both captured by `key`.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, maxAgeMs, refreshKey, persist]);

  if (!scope) return undefined;
  return result.key === key ? result.data : peekBackgroundRead<T>(scope, name)?.data;
}
